import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getAuthors,
  createAuthor,
  updateAuthor,
  deleteAuthor,
} from '../services/author_service'
import { useAuth } from '../context/AuthContext'
import './Author.css'

const emptyAuthorForm = {
  author_name: '',
  author_contact: '',
  author_address: '',
  author_gender: '',
}

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const getAuthorId = (author) => getValue(author, ['authorid', 'author_id'])

function Author() {
  const { user, hasPermission } = useAuth()
  const canAddAuthor = hasPermission('addAuthor')
  const canEditAuthor = hasPermission('editAuthor')
  const canDeleteAuthor = hasPermission('deleteAuthor')
  const loginUserId = user?.userid ?? user?.user_id ?? user?.id ?? ''
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [authors, setAuthors] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [authorModal, setAuthorModal] = useState(null)
  const [authorForm, setAuthorForm] = useState(emptyAuthorForm)
  const [isSavingAuthor, setIsSavingAuthor] = useState(false)
  const [authorFormError, setAuthorFormError] = useState('')
  const [pendingDeleteAuthor, setPendingDeleteAuthor] = useState(null)
  const [isDeletingAuthor, setIsDeletingAuthor] = useState(false)

  const loadAuthors = async (
    cursor = null,
    page = 1,
    query = appliedSearch,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getAuthors({ cursor, page, search: query })
      setAuthors(result.authors)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAuthors(null, 1, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchAuthors = (event) => {
    event.preventDefault()
    setAppliedSearch(search)
    setCursorHistory([])
    loadAuthors(null, 1, search)
  }
  const clearSearch = () => {
    setSearch('')
    setAppliedSearch('')
    setCursorHistory([])
    loadAuthors(null, 1, '')
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadAuthors(nextCursor, nextPage || currentPage, appliedSearch)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadAuthors(previous.cursor, previous.page, appliedSearch)
  }

  const openAddAuthorModal = () => {
    setAuthorFormError('')
    setAuthorForm(emptyAuthorForm)
    setAuthorModal({ mode: 'add' })
  }
  const openEditAuthorModal = (author) => {
    setAuthorFormError('')
    setAuthorForm({
      author_name: getValue(author, ['author_name'], ''),
      author_contact: getValue(author, ['author_contact'], ''),
      author_address: getValue(author, ['author_address'], ''),
      author_gender: getValue(author, ['author_gender'], ''),
    })
    setAuthorModal({ mode: 'edit', author })
  }
  const closeAuthorModal = () => {
    if (isSavingAuthor) return
    setAuthorModal(null)
  }
  const updateAuthorFormField = (event) => {
    const { name, value } = event.target
    setAuthorForm((current) => ({ ...current, [name]: value }))
  }
  const submitAuthorForm = async (event) => {
    event.preventDefault()
    setIsSavingAuthor(true)
    setAuthorFormError('')
    try {
      const payload = { ...authorForm, userid: loginUserId }
      if (authorModal?.mode === 'edit') {
        await updateAuthor(getAuthorId(authorModal.author), payload)
      } else {
        await createAuthor(payload)
      }
      setAuthorModal(null)
      await loadAuthors(currentCursor, currentPage, appliedSearch)
    } catch (requestError) {
      setAuthorFormError(requestError.message)
    } finally {
      setIsSavingAuthor(false)
    }
  }

  const confirmDeleteAuthor = async () => {
    if (!pendingDeleteAuthor) return
    setIsDeletingAuthor(true)
    setError('')
    try {
      await deleteAuthor(getAuthorId(pendingDeleteAuthor))
      setPendingDeleteAuthor(null)
      await loadAuthors(currentCursor, currentPage, appliedSearch)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeletingAuthor(false)
    }
  }

  return (
    <div className='authors-page'>
      <nav className='authors-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Authors</strong>
      </nav>
      <header className='authors-page__header'>
        <div>
          <span className='app-content__eyebrow'>Admin</span>
          <h1>Authors</h1>
          <p>Manage the list of authors used for book royalties.</p>
        </div>
        {canAddAuthor && (
          <button
            className='authors-button authors-button--primary'
            type='button'
            onClick={openAddAuthorModal}
          >
            + Add author
          </button>
        )}
      </header>
      {error && (
        <div className='authors-page__error' role='alert'>
          {error}
        </div>
      )}

      <section className='authors-search-card'>
        <form className='authors-search-form' onSubmit={searchAuthors}>
          <label>
            Search
            <input
              name='search'
              type='text'
              placeholder='Search by name or contact'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className='authors-search-form__actions'>
            <button
              className='authors-button authors-button--primary'
              type='submit'
            >
              Search
            </button>
            <button
              className='authors-button'
              type='button'
              onClick={clearSearch}
            >
              Clear
            </button>
          </div>
        </form>
      </section>

      <section
        className='authors-table-card'
        aria-labelledby='authors-table-title'
      >
        <div className='authors-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Author register</span>
            <h2 id='authors-table-title'>Author list</h2>
            <p>{authors.length} authors on this page</p>
          </div>
        </div>
        <div className='authors-table-wrap'>
          <table className='authors-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Gender</th>
                <th>Date added</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='authors-table__empty' colSpan='6'>
                    Loading authors...
                  </td>
                </tr>
              ) : authors.length ? (
                authors.map((author, index) => (
                  <tr key={`${getAuthorId(author)}-${index}`}>
                    <td>
                      <strong>{getValue(author, ['author_name'])}</strong>
                    </td>
                    <td>{getValue(author, ['author_contact'])}</td>
                    <td>{getValue(author, ['author_address'])}</td>
                    <td>{getValue(author, ['author_gender'])}</td>
                    <td>
                      {formatDate(getValue(author, ['author_date_added']))}
                    </td>
                    <td>
                      <div className='authors-actions'>
                        {canEditAuthor && (
                          <button
                            type='button'
                            onClick={() => openEditAuthorModal(author)}
                          >
                            Edit
                          </button>
                        )}
                        {canDeleteAuthor && (
                          <button
                            className='authors-actions__delete'
                            type='button'
                            onClick={() => setPendingDeleteAuthor(author)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='authors-table__empty' colSpan='6'>
                    No authors match the selected search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='authors-pagination'>
          <span>Showing up to 20 authors</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {authorModal && (
        <div
          className='authors-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeAuthorModal()
          }
        >
          <section
            className='authors-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='author-form-title'
          >
            <div className='authors-modal__header'>
              <div>
                <span className='app-content__eyebrow'>
                  {authorModal.mode === 'edit' ? 'Edit author' : 'Add author'}
                </span>
                <h2 id='author-form-title'>
                  {authorModal.mode === 'edit'
                    ? getValue(authorModal.author, ['author_name'])
                    : 'New author'}
                </h2>
              </div>
              <button
                type='button'
                onClick={closeAuthorModal}
                aria-label='Close author form'
                disabled={isSavingAuthor}
              >
                x
              </button>
            </div>
            {authorFormError && (
              <div className='authors-page__error' role='alert'>
                {authorFormError}
              </div>
            )}
            <form className='authors-form' onSubmit={submitAuthorForm}>
              <label className='authors-form__full'>
                Name
                <input
                  name='author_name'
                  type='text'
                  value={authorForm.author_name}
                  onChange={updateAuthorFormField}
                  required
                />
              </label>
              <label>
                Contact
                <input
                  name='author_contact'
                  type='text'
                  value={authorForm.author_contact}
                  onChange={updateAuthorFormField}
                  required
                />
              </label>
              <label>
                Gender
                <select
                  name='author_gender'
                  value={authorForm.author_gender}
                  onChange={updateAuthorFormField}
                >
                  <option value=''>Select gender</option>
                  <option value='Male'>Male</option>
                  <option value='Female'>Female</option>
                </select>
              </label>
              <label className='authors-form__full'>
                Address
                <textarea
                  name='author_address'
                  rows='3'
                  value={authorForm.author_address}
                  onChange={updateAuthorFormField}
                  required
                />
              </label>
              <div className='authors-form__actions'>
                <button
                  className='authors-button'
                  type='button'
                  onClick={closeAuthorModal}
                  disabled={isSavingAuthor}
                >
                  Cancel
                </button>
                <button
                  className='authors-button authors-button--primary'
                  type='submit'
                  disabled={isSavingAuthor}
                >
                  {isSavingAuthor ? 'Saving...' : 'Save author'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {pendingDeleteAuthor && (
        <div
          className='authors-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeletingAuthor &&
            setPendingDeleteAuthor(null)
          }
        >
          <section
            className='authors-modal'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-author-title'
            aria-describedby='delete-author-description'
          >
            <div className='authors-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Delete author</span>
                <h2 id='delete-author-title'>Delete author?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDeleteAuthor(null)}
                aria-label='Close delete confirmation'
                disabled={isDeletingAuthor}
              >
                x
              </button>
            </div>
            <p
              className='authors-modal__description'
              id='delete-author-description'
            >
              {getValue(pendingDeleteAuthor, ['author_name'])} will be
              permanently deleted. This action cannot be undone.
            </p>
            <div className='authors-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDeleteAuthor(null)}
                disabled={isDeletingAuthor}
              >
                Cancel
              </button>
              <button
                className='authors-modal__delete-button'
                type='button'
                onClick={confirmDeleteAuthor}
                disabled={isDeletingAuthor}
              >
                {isDeletingAuthor ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Author
